import React from 'react';
import { FileText, Heart, ChevronRight } from 'lucide-react';

/**
 * ChoiceGroupCard
 *
 * Container for a group of essay prompt options where students must choose one (or more)
 * Styled to match regular essay slot cards - half-width in grid, with same visual design
 */
function ChoiceGroupCard({ slots, onUpdateStatus, onDevelopIdea }) {
  if (!slots || slots.length === 0) {
    return null;
  }

  const groupLabel = slots[0]?.group_label || 'Essay Choice';
  const choiceType = slots[0]?.prompt_choice_type;
  const wordLimit = slots[0]?.word_limit;
  const isOptional = slots[0]?.is_optional;

  const finalizedSlots = slots.filter(s => s.selection_status === 'finalized');
  const exploratorySlots = slots.filter(s => s.selection_status === 'exploratory');
  const rejectedSlots = slots.filter(s => s.selection_status === 'rejected');

  // Get choice type display info
  const getChoiceTypeLabel = () => {
    if (choiceType === 'pick_one') {
      return 'Choose 1 Required';
    } else if (choiceType === 'optional_additional') {
      return 'Optional (Choose Any)';
    }
    return 'Essay Group';
  };

  return (
    <div className="bg-white border-2 border-slate-900 rounded-xl overflow-hidden hover:shadow-xl transition-all">
      {/* Card Header */}
      <div className="px-6 py-4 bg-stone-50 border-b border-stone-200">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 flex-1">
            <div className="w-2 h-2 rounded-full bg-blue-500"></div>
            <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wide">
              {getChoiceTypeLabel()}
            </h3>
          </div>
        </div>
      </div>

      {/* Card Body */}
      <div className="px-6 py-6">
        {/* Group Label */}
        <div className="mb-4">
          <h4 className="text-base font-semibold text-slate-900 mb-2">
            {groupLabel}
          </h4>
          <p className="text-xs text-slate-600">
            {choiceType === 'pick_one'
              ? 'Select exactly one prompt to develop'
              : 'You can develop one or more of these prompts'}
          </p>
        </div>

        {/* Word Limit */}
        {wordLimit && (
          <p className="text-xs text-stone-600 mb-4">
            <span className="font-semibold">Word Limit:</span> {wordLimit} words
          </p>
        )}

        {/* Optional Tag */}
        {isOptional && (
          <div className="inline-block px-2 py-1 bg-amber-100 text-amber-800 text-xs rounded mb-4">
            Optional Essay
          </div>
        )}

        {/* Finalized indicator */}
        {finalizedSlots.length > 0 && (
          <div className="flex items-center gap-2 mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center justify-center w-5 h-5 bg-green-500 rounded-full text-white text-xs font-bold">
              ✓
            </div>
            <span className="text-xs text-green-700">
              {choiceType === 'pick_one' ? (
                <>
                  <span className="font-semibold">Selected:</span> Option {finalizedSlots[0].prompt_option_id}
                </>
              ) : (
                <>
                  <span className="font-semibold">{finalizedSlots.length} prompt{finalizedSlots.length > 1 ? 's' : ''}</span> selected
                </>
              )}
            </span>
          </div>
        )}

        {/* Prompt Options */}
        <div className="space-y-3 mb-4">
          {slots.map((slot, idx) => {
            const isFinalized = slot.selection_status === 'finalized';
            const isRejected = slot.selection_status === 'rejected';

            return (
              <div
                key={slot.id}
                className={`border rounded-lg p-3 transition-all ${
                  isFinalized ? 'border-green-500 bg-green-50' :
                  isRejected ? 'border-stone-300 bg-stone-50 opacity-60' :
                  'border-slate-300 bg-white'
                }`}
              >
                <div className="flex items-start gap-2 mb-2">
                  <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    isFinalized ? 'bg-green-500 text-white' :
                    isRejected ? 'bg-stone-300 text-stone-600' :
                    'bg-slate-200 text-slate-700'
                  }`}>
                    {isFinalized ? '✓' : slot.prompt_option_id}
                  </div>
                  <div className="flex-1">
                    <p className={`text-xs leading-relaxed ${
                      isRejected ? 'text-stone-500' : 'text-slate-700'
                    }`}>
                      {slot.prompt_text}
                    </p>

                    {/* Status badge */}
                    {isFinalized && (
                      <div className="mt-2 inline-block px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded">
                        Final Choice
                      </div>
                    )}
                    {isRejected && (
                      <div className="mt-2 inline-block px-2 py-0.5 bg-stone-200 text-stone-600 text-xs font-medium rounded">
                        Not Pursuing
                      </div>
                    )}
                    {!isFinalized && !isRejected && (
                      <div className="mt-2 inline-block px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                        Exploring
                      </div>
                    )}
                  </div>
                </div>

                {/* Action buttons for each option */}
                <div className="flex items-center gap-2 mt-3">
                  <button
                    onClick={() => onDevelopIdea && onDevelopIdea(slot)}
                    className="flex-1 px-3 py-1.5 bg-slate-900 text-white rounded hover:bg-slate-800 transition-colors text-xs font-medium flex items-center justify-center gap-1.5"
                  >
                    <FileText className="w-3 h-3" />
                    Develop Idea
                  </button>

                  {!isRejected && (
                    <button
                      onClick={() => onUpdateStatus && onUpdateStatus(slot.id, 'rejected')}
                      className="px-3 py-1.5 bg-white border border-stone-300 text-stone-600 rounded hover:bg-stone-50 transition-colors text-xs font-medium"
                    >
                      Not Pursuing
                    </button>
                  )}

                  {isRejected && (
                    <button
                      onClick={() => onUpdateStatus && onUpdateStatus(slot.id, 'exploratory')}
                      className="px-3 py-1.5 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors text-xs font-medium"
                    >
                      Restore
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Help text */}
        <div className="mt-4 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-slate-600">
          💡 <span className="font-medium">Tip:</span> Click "Develop Idea" on any option to start brainstorming.
          {choiceType === 'pick_one' && ' You can explore multiple prompts before deciding on your final choice.'}
        </div>
      </div>
    </div>
  );
}

export default ChoiceGroupCard;
